import {
  DEFAULT_MODEL_TRY_ON_ETHNICITY,
  DEFAULT_MODEL_TRY_ON_MODEL_TYPE,
} from '@/pages/ipe/workbench/constants/modelStudioConfig';
import {
  resolveTryOnReuseModelState,
} from '@/pages/ipe/workbench/constants/modelTryOnPresetConfig';
import {
  resolveEthnicityPresetFeatureCode,
  resolveModelEthnicityPresetFromParams,
  resolveModelEthnicityReuseModelState,
} from './systemModelEthnicityPresetCatalog';
import {
  buildTryOnTaskModelParams,
  resolveTryOnPresetFromParams,
  resolveTryOnPresetPersistedUrl,
} from './tryOnSystemModelPresetFallback';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';

const MODEL_STUDIO_NAV_IDS = ['modelTryOn', 'modelHandheld', 'modelUniversalWear'];

function isModelStudioNav(navId) {
  return MODEL_STUDIO_NAV_IDS.includes(navId);
}

function resolveReuseModelState(navId, result) {
  if (navId === 'modelTryOn') return resolveTryOnReuseModelState(result);
  if (navId === 'modelHandheld' || navId === 'modelUniversalWear') {
    return resolveModelEthnicityReuseModelState(result);
  }
  return { usedUploadedModel: false, selectedPresetId: '' };
}

/** 从任务 result 提取模特步骤参数字段（保留旧 presetId / legacy URL）。 */
export function extractModelStudioPresetParamsFromResult(navId, result = {}) {
  if (!isModelStudioNav(navId)) return null;
  const r = result && typeof result === 'object' ? result : {};
  const reuseModel = resolveReuseModelState(navId, r);
  const modelType = String(r.modelType || DEFAULT_MODEL_TRY_ON_MODEL_TYPE).trim()
    || DEFAULT_MODEL_TRY_ON_MODEL_TYPE;

  if (reuseModel.usedUploadedModel) {
    const modelImageUrl = String(r.modelImageUrl || '').trim();
    if (!modelImageUrl) return null;
    return {
      usedUploadedModel: true,
      selectedPresetId: '',
      selectedPresetUrl: '',
      modelImageUrl,
      modelImagePreviewUrl: String(r.modelImagePreviewUrl || r.modelImageUrl || '').trim(),
      modelType,
      ...(navId === 'modelTryOn'
        ? {
          garmentType: String(r.garmentType || '').trim(),
          frameScope: String(r.frameScope || '').trim(),
        }
        : { modelEthnicity: String(r.modelEthnicity || DEFAULT_MODEL_TRY_ON_ETHNICITY).trim() }),
    };
  }

  const selectedPresetId = String(reuseModel.selectedPresetId || r.selectedPresetId || '').trim();
  const selectedPresetUrl = String(r.selectedPresetUrl || '').trim();
  if (!selectedPresetId && !selectedPresetUrl) return null;

  return {
    usedUploadedModel: false,
    selectedPresetId,
    selectedPresetUrl,
    modelImageUrl: '',
    modelImagePreviewUrl: '',
    modelType,
    ...(navId === 'modelTryOn'
      ? {
        garmentType: String(r.garmentType || '').trim(),
        frameScope: String(r.frameScope || '').trim(),
      }
      : { modelEthnicity: String(r.modelEthnicity || DEFAULT_MODEL_TRY_ON_ETHNICITY).trim() }),
  };
}

function extractModelStudioPresetParamsFromStepRecord(navId, stepRec = {}, rowResult = {}) {
  if (!isModelStudioNav(navId) || !stepRec) return null;
  if (stepRec.modelReferenceUsedUploadedModel === true) {
    const modelImageUrl = String(rowResult?.modelImageUrl || '').trim();
    if (!modelImageUrl) return null;
    return extractModelStudioPresetParamsFromResult(navId, {
      ...rowResult,
      usedUploadedModel: true,
      modelImageUrl,
    });
  }
  const presetId = String(stepRec.modelReferencePresetId || '').trim();
  if (!presetId) return null;
  return extractModelStudioPresetParamsFromResult(navId, {
    ...rowResult,
    usedUploadedModel: false,
    selectedPresetId: presetId,
    selectedPresetUrl: rowResult?.selectedPresetUrl || '',
    modelType: stepRec.modelReferenceModelType || rowResult?.modelType,
    garmentType: stepRec.modelReferenceGarmentType || rowResult?.garmentType,
    modelEthnicity: stepRec.modelReferenceGarmentType || rowResult?.modelEthnicity,
    frameScope: rowResult?.frameScope,
  });
}

function hasModelStudioPresetSelection(params = {}) {
  if (params.usedUploadedModel === true) {
    return Boolean(String(params.modelImageUrl || '').trim());
  }
  return Boolean(
    String(params.selectedPresetId || '').trim()
    || String(params.selectedPresetUrl || '').trim(),
  );
}

/**
 * 历史工作流：当 runtimeStepOverrides 缺少模特参数时，从已完成条目的 stepDisplay 回填。
 */
export function hydrateModelStudioRuntimeOverrides(template, runtimeOverrides = {}, resultEntries = []) {
  const hydrated = { ...(runtimeOverrides || {}) };
  const steps = Array.isArray(template?.steps) ? template.steps : [];

  MODEL_STUDIO_NAV_IDS.forEach((navId) => {
    if (!steps.some((step) => step.navId === navId)) return;

    const templateParams = steps.find((step) => step.navId === navId)?.params || {};
    const current = hydrated[navId] && typeof hydrated[navId] === 'object'
      ? hydrated[navId]
      : {};
    const merged = { ...templateParams, ...current };
    if (hasModelStudioPresetSelection(merged)) {
      hydrated[navId] = merged;
      return;
    }

    for (const entry of resultEntries) {
      const stepDisplay = entry?.stepDisplay;
      if (!Array.isArray(stepDisplay)) continue;
      const stepRec = stepDisplay.find((item) => item?.navId === navId);
      if (!stepRec) continue;
      const extracted = extractModelStudioPresetParamsFromStepRecord(
        navId,
        stepRec,
        entry?.result,
      );
      if (extracted && hasModelStudioPresetSelection(extracted)) {
        hydrated[navId] = { ...merged, ...extracted };
        break;
      }
    }
  });

  return hydrated;
}

async function resolveUploadedModelImageUrl(result = {}) {
  const raw = String(result.modelImageUrl || '').trim();
  if (!raw) return '';
  return resolveImg2ImgReferenceForApi(raw) || '';
}

async function resolveSystemPresetForNav(navId, params = {}) {
  let preset = null;
  if (navId === 'modelTryOn') {
    preset = await resolveTryOnPresetFromParams(
      params.modelType,
      params.garmentType,
      params,
    );
  } else {
    preset = await resolveModelEthnicityPresetFromParams(
      resolveEthnicityPresetFeatureCode(navId),
      params.modelType,
      params.modelEthnicity,
      params,
    );
  }
  if (!preset) return null;

  const persistedUrl = resolveTryOnPresetPersistedUrl(preset) || String(preset.url || '').trim();
  if (!persistedUrl) {
    return { preset, modelImageUrl: '' };
  }
  const modelImageUrl = await resolveImg2ImgReferenceForApi(persistedUrl);
  return { preset, modelImageUrl: modelImageUrl || '' };
}

function buildSystemPresetMeta(navId, params, preset, modelFields) {
  const presetId = String(preset?.id || preset?.legacyPresetId || params.selectedPresetId || '').trim();
  const selectedPresetUrl = resolveTryOnPresetPersistedUrl(preset)
    || String(params.selectedPresetUrl || preset?.url || '').trim();

  return {
    ...modelFields,
    usedUploadedModel: false,
    selectedPresetId: presetId,
    selectedPresetUrl,
    hasCustomModel: params.hasCustomModel === true
      || Boolean(presetId)
      || Boolean(selectedPresetUrl),
    modelType: params.modelType,
    ...(navId === 'modelTryOn'
      ? {
        garmentType: params.garmentType,
        frameScope: params.frameScope,
      }
      : { modelEthnicity: params.modelEthnicity }),
  };
}

/**
 * 历史 AI 工作室任务重试 / 工作流复用：解析系统模特或上传模特。
 * selectedPresetId 支持 API UUID、legacy id、静态 id；优先 API catalog，其次 legacy URL。
 */
export async function resolveModelStudioPresetForRetry(navId, result = {}, options = {}) {
  void options;
  if (!isModelStudioNav(navId)) {
    return {
      modelImageUrl: null,
      meta: {
        usedUploadedModel: false,
        selectedPresetId: '',
        selectedPresetUrl: '',
        hasCustomModel: false,
      },
    };
  }

  const r = result && typeof result === 'object' ? result : {};
  const params = extractModelStudioPresetParamsFromResult(navId, r);
  const reuseModel = resolveReuseModelState(navId, r);

  if (reuseModel.usedUploadedModel) {
    const modelImageUrl = await resolveUploadedModelImageUrl(r);
    const modelFields = buildTryOnTaskModelParams({
      uploadedModelRef: String(r.modelImageUrl || '').trim()
        ? { apiUrl: String(r.modelImageUrl).trim() }
        : null,
    });
    return {
      modelImageUrl: modelImageUrl || null,
      meta: {
        ...modelFields,
        hasCustomModel: true,
        modelType: params?.modelType || r.modelType,
        ...(navId === 'modelTryOn'
          ? {
            garmentType: params?.garmentType || r.garmentType,
            frameScope: params?.frameScope || r.frameScope,
          }
          : { modelEthnicity: params?.modelEthnicity || r.modelEthnicity }),
      },
    };
  }

  if (!params || !hasModelStudioPresetSelection(params)) {
    return {
      modelImageUrl: null,
      meta: {
        usedUploadedModel: false,
        selectedPresetId: '',
        selectedPresetUrl: '',
        hasCustomModel: false,
        modelType: params?.modelType || r.modelType,
        ...(navId === 'modelTryOn'
          ? { garmentType: params?.garmentType || r.garmentType }
          : { modelEthnicity: params?.modelEthnicity || r.modelEthnicity }),
      },
    };
  }

  const resolved = await resolveSystemPresetForNav(navId, {
    ...params,
    hasCustomModel: r.hasCustomModel,
  });
  if (resolved?.preset) {
    const modelFields = buildTryOnTaskModelParams({ systemPreset: resolved.preset });
    return {
      modelImageUrl: resolved.modelImageUrl || null,
      meta: buildSystemPresetMeta(navId, params, resolved.preset, modelFields),
    };
  }

  const legacyUrl = String(params.selectedPresetUrl || r.modelImageUrl || '').trim();
  const modelImageUrl = legacyUrl ? await resolveImg2ImgReferenceForApi(legacyUrl) : '';
  return {
    modelImageUrl: modelImageUrl || null,
    meta: {
      usedUploadedModel: false,
      selectedPresetId: params.selectedPresetId,
      selectedPresetUrl: legacyUrl,
      hasCustomModel: r.hasCustomModel === true || Boolean(params.selectedPresetId) || Boolean(legacyUrl),
      modelType: params.modelType,
      ...(navId === 'modelTryOn'
        ? {
          garmentType: params.garmentType,
          frameScope: params.frameScope || r.frameScope,
        }
        : { modelEthnicity: params.modelEthnicity }),
    },
  };
}
